import React from 'react';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import chroma from 'chroma-js';
import NetworkManager from '../../app/managers/networkManager';
import { getCollabInitials } from '../../app/utils/helpers';

/**
 * A collab's profile picture, with initials as the fallback.
 *
 * Presentational: it takes a collab's fields and reads nothing from the store, so the same
 * component serves the planning rows, the profile modal and anywhere else a face is wanted.
 *
 * Two behaviours worth knowing about:
 *
 *  - `photoUpdatedAt` decides everything. Null means the collab has no picture, and the
 *    component renders initials without issuing a request — which is why listings return
 *    that column. When it is set, it also versions the image URL, so a new upload is picked
 *    up immediately while an unchanged one is served from the browser's disk cache.
 *
 *  - The image is loaded lazily and MUI's Avatar swaps back to the initials on its own if
 *    the request fails, so a deleted or unreadable picture degrades to initials instead of
 *    a broken-image icon.
 */

// Fallback when a collab has no `color` set. MUI's default avatar grey.
const DEFAULT_BACKGROUND = '#bdbdbd';

/**
 * Black or white, whichever is actually readable on the given background.
 *
 * Collab colours are picked by hand in the app and range from very dark to very light, so
 * hardcoding white text makes the initials vanish on the pale ones.
 */
const readableTextColor = (background) => {
    try {
        return chroma.contrast(background, '#ffffff') >= 3 ? '#ffffff' : '#000000';
    } catch (error) {
        // chroma throws on an unparseable colour string; a collab colour is free text in the
        // database, so this is a real possibility rather than a theoretical one.
        return '#ffffff';
    }
};

export function CollabAvatar({
    id,
    nom,
    prenom,
    photoUpdatedAt,
    color,
    size = 32,
    showTooltip = false,
    sx = {},
    ...rest
}) {

    const photoUrl = NetworkManager.getInstance().getCollabPhotoUrl(id, photoUpdatedAt);
    const initials = getCollabInitials(prenom, nom);
    const fullName = `${prenom || ''} ${nom || ''}`.trim();

    const background = color || DEFAULT_BACKGROUND;

    const avatar = (
        <Avatar
            // `src` must be undefined rather than null when there is no picture, otherwise
            // MUI still renders an <img> and the browser requests the empty URL.
            src={photoUrl || undefined}
            alt={fullName}
            imgProps={{
                loading: 'lazy',
                // A planning row can hold hundreds of these; decoding off the main thread
                // keeps scrolling from stuttering while they arrive.
                decoding: 'async',
            }}
            sx={{
                width: size,
                height: size,
                // Only meaningful behind the initials — an loaded image covers it entirely.
                bgcolor: background,
                color: readableTextColor(background),
                // Scales with the avatar so one component works at 24px in a row and 128px
                // in the profile modal.
                fontSize: Math.max(10, Math.round(size * 0.4)),
                fontFamily: 'roboto-bold',
                ...sx,
            }}
            {...rest}
        >
            {initials}
        </Avatar>
    );

    // Off by default: on the planning the name is already next to the face, so a tooltip
    // repeating it is noise. Wanted where the avatar stands alone.
    if (showTooltip && fullName) {
        return <Tooltip title={fullName}>{avatar}</Tooltip>;
    }

    return avatar;
}

export default CollabAvatar;
